import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import styles from './ProjectsPage.module.css';
import { useLang } from '../../context/LanguageContext';

const CATEGORIES = [
  {
    id: 'web',
    label: { fr: 'Sites web', en: 'Websites' },
    projects: [
      {
        id: 'dhr',
        name: 'DHR Structures',
        sector: { fr: 'Ingénierie & Construction', en: 'Engineering & Construction' },
        year: '2025',
        img: '/projects/dhr.png',
        url: 'https://dhrstructures.ca',
        desc: {
          fr: 'Site institutionnel, SEO technique et messagerie professionnelle pour un cabinet BIM de Sherbrooke.',
          en: 'Institutional website, technical SEO and professional email setup for a BIM firm in Sherbrooke.',
        },
        tags: ['Next.js', 'SEO', 'Infrastructure'],
      },
      {
        id: 'mack',
        name: 'Mack Consulting',
        sector: { fr: 'Coaching & Leadership', en: 'Coaching & Leadership' },
        year: '2026',
        img: '/projects/mack.png',
        url: 'https://mackconsulting.ca',
        desc: {
          fr: 'Site vitrine bilingue pour Christine Mack, coach exécutive certifiée et praticienne en neurosciences.',
          en: 'Bilingual showcase site for Christine Mack, certified executive coach and neuroscience practitioner.',
        },
        tags: ['Site vitrine', 'FR/EN', 'SEO'],
      },
      {
        id: 'bbr',
        name: 'BBR Inc.',
        sector: { fr: 'Vente directe & représentation de marque', en: 'Direct Sales & Brand Representation' },
        year: '2026',
        img: '/projects/BBRinc.png',
        url: 'https://bbr-drab.vercel.app/',
        desc: {
          fr: 'Site bilingue pour BBR Inc., qui accompagne les marques en vente directe, acquisition client et représentation sur le terrain à London, en Ontario.',
          en: 'Bilingual website for BBR Inc., helping brands with direct sales, customer acquisition and in-person representation in London, Ontario.',
        },
        tags: ['Site vitrine', 'FR/EN', 'Acquisition client'],
      },
      {
        id: 'ecolis',
        name: 'Ecolis',
        sector: { fr: 'Transport collaboratif', en: 'Collaborative Delivery' },
        year: '2026',
        img: '/projects/ecolis.png',
        url: 'https://ecolis-apps.com',
        desc: {
          fr: 'Application qui met en relation les personnes souhaitant envoyer un colis et les voyageurs disposant d’espace dans leurs bagages.',
          en: 'An app connecting people who need to send a parcel with travelers who have room in their luggage.',
        },
        tags: ['Application', 'Mise en relation', 'FR/EN/AR'],
      },
    ],
  },
];

const TERMINAL_LINES = [
  { text: '$ axisdev init votre-projet', type: 'cmd' },
  { text: '> Analyse du besoin métier...', type: 'info' },
  { text: '> Architecture définie.', type: 'success' },
  { text: '$ axisdev build --production', type: 'cmd' },
  { text: '> Tests & déploiement...', type: 'info' },
  { text: '✓ Projet livré. En ligne.', type: 'done' },
];

const fadeUp = (i = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: i * 0.1, duration: 0.55 },
});

const ProjectsPage = () => {
  const { t, lang } = useLang();
  const p = t.projects;
  return (
  <div className={styles.page}>
    <div className={styles.gridBg} />

    <section className={styles.hero}>
      <div className={styles.container}>
        <motion.div {...fadeUp(0)}>
          <p className={styles.eyebrow}>{p.eyebrow}</p>
          <h1 className={styles.title}>{p.title}</h1>
          <p className={styles.sub}>{p.sub}</p>
        </motion.div>
      </div>
    </section>

    <section className={styles.content}>
      <div className={styles.container}>
        {CATEGORIES.map((cat, ci) => (
          <motion.div key={cat.id} className={styles.categoryGroup} {...fadeUp(ci * 0.1 + 0.2)}>
            <div className={styles.categoryHeader}>
              <span className={styles.categoryNum}>0{ci + 1}</span>
              <h2 className={styles.categoryLabel}>{cat.label[lang]}</h2>
              <span className={styles.categoryCount}>{cat.projects.length} projet{cat.projects.length > 1 ? 's' : ''}</span>
            </div>
            <div className={styles.projectGrid}>
              {cat.projects.map((proj, pi) => (
                <motion.article key={proj.id} className={styles.card} {...fadeUp(ci * 0.1 + pi * 0.12 + 0.3)}>
                  <div className={styles.imgWrap}>
                    <img src={proj.img} alt={proj.name} className={styles.img} />
                    <div className={styles.imgOverlay} />
                    <span className={styles.yearBadge}>{proj.year}</span>
                  </div>
                  <div className={styles.cardBody}>
                    <p className={styles.sector}>{proj.sector[lang]}</p>
                    <h3 className={styles.name}>{proj.name}</h3>
                    <p className={styles.desc}>{proj.desc[lang]}</p>
                    <div className={styles.tags}>
                      {proj.tags.map(tag => <span key={tag} className={styles.tag}>{tag}</span>)}
                    </div>
                    <a href={proj.url} target="_blank" rel="noopener noreferrer" className={styles.visitBtn}>
                      {p.visitSite}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                      </svg>
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          </motion.div>
        ))}

        <motion.div className={styles.teaser}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Terminal */}
          <div className={styles.terminal}>
            <div className={styles.termBar}>
              <div className={styles.termDots}>
                <span className={styles.dot} style={{ background: '#FF5F57' }} />
                <span className={styles.dot} style={{ background: '#FEBC2E' }} />
                <span className={styles.dot} style={{ background: '#28C840' }} />
              </div>
              <span className={styles.termTitle}>axisdev — terminal</span>
            </div>
            <div className={styles.termBody}>
              {TERMINAL_LINES.map((line, i) => (
                <motion.div
                  key={i}
                  className={`${styles.termLine} ${styles['termLine_' + line.type]}`}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + i * 0.22, duration: 0.35 }}
                >
                  {line.text}
                </motion.div>
              ))}
              <motion.span
                className={styles.cursor}
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >▋</motion.span>
            </div>
          </div>

          {/* Info */}
          <div className={styles.teaserInfo}>
            <span className={styles.teaserTag}>{p.teaserTag}</span>
            <p className={styles.teaserText}>{p.teaserText.split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
            ))}</p>
            <Link to="/contact" className={styles.teaserCta}>
              {p.teaserCta}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  </div>
  );
};

export default ProjectsPage;
